/*
 * Close-readiness / settle-up-completeness predicate (ADR-0030, supersedes
 * ADR-0029 §2).
 *
 * A receipt is READY — reflected in the settle-up plan and done — only when it
 * is human-CONFIRMED and has a payer and a currency. A receipt NEEDS ATTENTION
 * iff any of these does not hold:
 *
 *     status !== "confirmed"               (still processing, failed, or
 *                                           parsed-but-not-yet-confirmed)
 *     OR paid_by_participant_id is null
 *     OR source_ccy is null
 *
 * ADR-0030 tightened this: previously a `parsed` receipt with a payer and a
 * currency was treated as reflected (the payer absorbed an unsplit cost). We no
 * longer consider a receipt done until a human has confirmed it — an unconfirmed
 * receipt still carries an unreviewed AI parse. The two axes are independent: a
 * receipt can lack a payer AND be unconfirmed; either alone is enough to need
 * attention. There is deliberately no "no consumers" case (the SPA has no
 * consumer data on the receipts list anyway).
 *
 * The whole signal is derived in the SPA from the (non-paginated) receipts
 * list. There is no server readiness field (ADR-0029 §1).
 *
 * One predicate feeds three surfaces: the <IncompleteReceiptsNotice> on
 * «Розрахунок», the ConfirmCloseTrip block-gate checklist, and the filtered
 * «потребують уваги» receipts view.
 */

import type { Receipt } from "@/api/types";

/** Product-level reason buckets for a needs-attention receipt. */
export type AttentionReason =
  | "processing" // R-PROCESSING: pending_upload | parsing — still working
  | "failed" // R-FAILED: parse_failed — could not recognise
  | "noPayer" // R-NO-PAYER: paid_by_participant_id is null
  | "noCcy" // R-NO-CCY: source_ccy is null
  | "unconfirmed"; // R-UNCONFIRMED: parsed, has payer+ccy, but not confirmed

const PROCESSING: ReadonlySet<Receipt["status"]> = new Set([
  "pending_upload",
  "parsing",
]);

/** ADR-0030 predicate. True when the receipt is NOT reflected in the plan. */
export function needsAttention(r: Receipt): boolean {
  return (
    r.status !== "confirmed" ||
    r.paid_by_participant_id == null ||
    r.source_ccy == null
  );
}

/**
 * The dominant reason a receipt needs attention, in priority order so each
 * receipt maps to exactly one bucket for the checklist / hint copy:
 * processing > failed (terminal-blocking states first), then the
 * missing-field reasons (no-payer before no-currency), and finally
 * unconfirmed — the residual case of a parsed receipt that already has a
 * payer and a currency but still awaits a human confirm.
 */
export function attentionReason(r: Receipt): AttentionReason | null {
  if (PROCESSING.has(r.status)) return "processing";
  if (r.status === "parse_failed") return "failed";
  if (r.paid_by_participant_id == null) return "noPayer";
  if (r.source_ccy == null) return "noCcy";
  if (r.status !== "confirmed") return "unconfirmed";
  return null;
}

export interface ReadinessCounts {
  processing: number;
  failed: number;
  noPayer: number;
  noCcy: number;
  unconfirmed: number;
  /** Total needs-attention receipts (sum of the five buckets). */
  total: number;
}

export interface Readiness {
  counts: ReadinessCounts;
  /** Receipt ids that need attention, in list order. */
  blockingIds: string[];
  /**
   * True when every needs-attention receipt is still processing
   * (pending_upload/parsing) — nothing failed and no missing field. Drives the
   * softer "ще обробляється" copy with no fix-link.
   */
  transientOnly: boolean;
}

/** Derives the full readiness signal from the receipts list (ADR-0029 §1). */
export function deriveReadiness(receipts: ReadonlyArray<Receipt>): Readiness {
  const counts: ReadinessCounts = {
    processing: 0,
    failed: 0,
    noPayer: 0,
    noCcy: 0,
    unconfirmed: 0,
    total: 0,
  };
  const blockingIds: string[] = [];

  for (const r of receipts) {
    const reason = attentionReason(r);
    if (reason == null) continue;
    blockingIds.push(r.receipt_id);
    counts.total += 1;
    switch (reason) {
      case "processing":
        counts.processing += 1;
        break;
      case "failed":
        counts.failed += 1;
        break;
      case "noPayer":
        counts.noPayer += 1;
        break;
      case "noCcy":
        counts.noCcy += 1;
        break;
      case "unconfirmed":
        counts.unconfirmed += 1;
        break;
    }
  }

  // Transient-only when the only reasons present are still-processing ones.
  const transientOnly =
    counts.total > 0 && counts.processing === counts.total;

  return { counts, blockingIds, transientOnly };
}

/** Short reason label for the filtered-list soft hint + checklist rows. */
export function attentionReasonLabel(reason: AttentionReason): string {
  switch (reason) {
    case "processing":
      return "ще обробляється";
    case "failed":
      return "не вдалося розпізнати";
    case "noPayer":
      return "не вказано, хто платив";
    case "noCcy":
      return "не вибрано валюту";
    case "unconfirmed":
      return "не підтверджено";
  }
}
